// catalog.ts
import {
  InjectionToken,
  Pipe,
  PipeTransform,
  inject,
  LOCALE_ID,
} from "@angular/core";

/* 1) Domaines */
export const DOMAIN = {
  CONDITION: "CONDITION",
  AVAILABILITY: "AVAILABILITY",
  FLIGHT_TYPE: "FLIGHT_TYPE",
  FLIGHT_PRICE_CATEGORY: "FLIGHT_PRICE_CATEGORY",
  LANDING_APPROACH_TYPE: "LANDING_APPROACH_TYPE",
} as const;
export type Domain = (typeof DOMAIN)[keyof typeof DOMAIN];

/* 2) Codes “enum-like” par domaine (utilisables comme CODES.CONDITION.AIRWORTHY) */
export const CODES = {
  CONDITION: {
    AIRWORTHY: "AIRWORTHY",
    MAINTENANCE_DUE: "MAINTENANCE_DUE",
    GROUNDED: "GROUNDED",
    FINDINGS: "FINDINGS",
  } as const,
  AVAILABILITY: {
    AVAILABLE: "AVAILABLE",
    UNAVAILABLE: "UNAVAILABLE",
    MIXED: "MIXED",
  } as const,
  FLIGHT_TYPE: {
    TRAINING: "TRAINING",
    SOLO: "SOLO",
    XC: "XC",
  } as const,
  FLIGHT_PRICE_CATEGORY: {
    NONE: "NO_INSTRUCTOR",
    BASIC: "BASIC_INSTRUCTION",
    SOLO_SUP: "SOLO_SUPERVISION",
    NIGHT: "NIGHT_INSTRUCTION",
    FI_I: "FI_INSTRUCTION",
    IR: "IR_INSTRUCTION",
  } as const,
  LANDING_APPROACH_TYPE: {
    VISUAL: "VISUAL",
    PA_3D: "PROC_3D",
    NPA_2D: "PROC_2D",
  } as const,
} as const;

/* 3) Types de codes (union de leurs valeurs) */
export type ConditionCode =
  (typeof CODES.CONDITION)[keyof typeof CODES.CONDITION];
export type AvailabilityCode =
  (typeof CODES.AVAILABILITY)[keyof typeof CODES.AVAILABILITY];
export type FlightTypeCode =
  (typeof CODES.FLIGHT_TYPE)[keyof typeof CODES.FLIGHT_TYPE];
export type FlightPriceCategory =
  (typeof CODES.FLIGHT_PRICE_CATEGORY)[keyof typeof CODES.FLIGHT_PRICE_CATEGORY];
export type LandingApproachType =
  (typeof CODES.LANDING_APPROACH_TYPE)[keyof typeof CODES.LANDING_APPROACH_TYPE];

/* 4) Vue affichée pour chaque code */
export type View = {
  label: string;
  color?: string; // bg
  text?: string; // fg
  icon?: string;
  order?: number;
  className?: string; // classe CSS (ex: chip)
};

/* 5) Registre typé par domaine */
export type Registry = {
  [DOMAIN.CONDITION]: Record<ConditionCode, View>;
  [DOMAIN.AVAILABILITY]: Record<AvailabilityCode, View>;
  [DOMAIN.FLIGHT_TYPE]: Record<FlightTypeCode, View>;
  [DOMAIN.FLIGHT_PRICE_CATEGORY]: Record<FlightPriceCategory, View>;
  [DOMAIN.LANDING_APPROACH_TYPE]: Record<LandingApproachType, View>;
};

/* 6) Provider de catalogues multi-langues (remplaçable si besoin) */
export interface CatalogProvider {
  get(locale: string): Registry | null | undefined;
}
export const CATALOG_PROVIDER = new InjectionToken<CatalogProvider>(
  "CATALOG_PROVIDER"
);

/* 7) Catalogues en mémoire par langue (FR/EN). Tu peux en ajouter d'autres. */
const FR: Registry = {
  [DOMAIN.CONDITION]: {
    [CODES.CONDITION.AIRWORTHY]: {
      label: "En état de vol",
      color: "#4caf50",
      text: "#fff",
      icon: "flight",
      order: 0,
      className: "chip-airworthy",
    },
    [CODES.CONDITION.MAINTENANCE_DUE]: {
      label: "Maintenance",
      color: "#ff9800",
      text: "#111",
      icon: "build",
      order: 1,
      className: "chip-maintenance",
    },
    [CODES.CONDITION.GROUNDED]: {
      label: "Cloué au sol",
      color: "#f44336",
      text: "#fff",
      icon: "report",
      order: 2,
      className: "chip-grounded",
    },
    [CODES.CONDITION.FINDINGS]: {
      label: "Observations",
      color: "#2196f3",
      text: "#fff",
      icon: "info",
      order: 3,
      className: "chip-findings",
    },
  },
  [DOMAIN.AVAILABILITY]: {
    [CODES.AVAILABILITY.AVAILABLE]: {
      label: "Disponible",
      color: "rgba(76,175,80,.18)",
      text: "#2e7d32",
      order: 0,
    },
    [CODES.AVAILABILITY.UNAVAILABLE]: {
      label: "Indisponible",
      color: "rgba(244,67,54,.18)",
      text: "#b71c1c",
      order: 1,
    },
    [CODES.AVAILABILITY.MIXED]: {
      label: "Partielle",
      color: "rgba(255,193,7,.18)",
      text: "#e65100",
      order: 2,
    },
  },
  [DOMAIN.FLIGHT_TYPE]: {
    [CODES.FLIGHT_TYPE.TRAINING]: {
      label: "Instruction",
      color: "#90caf9",
      text: "#0d47a1",
      order: 0,
    },
    [CODES.FLIGHT_TYPE.SOLO]: {
      label: "Solo",
      color: "#a5d6a7",
      text: "#1b5e20",
      order: 1,
    },
    [CODES.FLIGHT_TYPE.XC]: {
      label: "Navigation",
      color: "#ffe082",
      text: "#e65100",
      order: 2,
    },
  },
  [DOMAIN.FLIGHT_PRICE_CATEGORY]: {
    [CODES.FLIGHT_PRICE_CATEGORY.NONE]: {
      label: "Pas d'instructeur",
      color: "rgba(76,175,80,.18)",
      text: "#2e7d32",
      order: 0,
    },
    [CODES.FLIGHT_PRICE_CATEGORY.BASIC]: {
      label: "Instruction de base",
      color: "rgba(76,175,80,.18)",
      text: "#2e7d32",
      order: 0,
    },
    [CODES.FLIGHT_PRICE_CATEGORY.SOLO_SUP]: {
      label: "Supervision Solo",
      color: "rgba(76,175,80,.18)",
      text: "#2e7d32",
      order: 0,
    },
    [CODES.FLIGHT_PRICE_CATEGORY.NIGHT]: {
      label: "Instruction de nuit",
      color: "rgba(76,175,80,.18)",
      text: "#2e7d32",
      order: 0,
    },
    [CODES.FLIGHT_PRICE_CATEGORY.FI_I]: {
      label: "Instruction FI-I",
      color: "rgba(76,175,80,.18)",
      text: "#2e7d32",
      order: 0,
    },
    [CODES.FLIGHT_PRICE_CATEGORY.IR]: {
      label: "Instruction IR",
      color: "rgba(76,175,80,.18)",
      text: "#2e7d32",
      order: 0,
    },
  },
  [DOMAIN.LANDING_APPROACH_TYPE]: {
    [CODES.LANDING_APPROACH_TYPE.VISUAL]: {
      label: "Visual",
      color: "rgba(76,175,80,.18)",
      text: "#2e7d32",
      order: 0,
    },
    [CODES.LANDING_APPROACH_TYPE.PA_3D]: {
      label: "Procédure 3D",
      color: "rgba(76,175,80,.18)",
      text: "#2e7d32",
      order: 0,
    },
    [CODES.LANDING_APPROACH_TYPE.NPA_2D]: {
      label: "Procédure 2D",
      color: "rgba(76,175,80,.18)",
      text: "#2e7d32",
      order: 0,
    },
  },
};

const EN: Registry = {
  [DOMAIN.CONDITION]: {
    [CODES.CONDITION.AIRWORTHY]: {
      label: "Airworthy",
      color: "#4caf50",
      text: "#fff",
      icon: "flight",
      order: 0,
      className: "chip-airworthy",
    },
    [CODES.CONDITION.MAINTENANCE_DUE]: {
      label: "Maintenance",
      color: "#ff9800",
      text: "#111",
      icon: "build",
      order: 1,
      className: "chip-maintenance",
    },
    [CODES.CONDITION.GROUNDED]: {
      label: "Grounded",
      color: "#f44336",
      text: "#fff",
      icon: "report",
      order: 2,
      className: "chip-grounded",
    },
    [CODES.CONDITION.FINDINGS]: {
      label: "Findings",
      color: "#2196f3",
      text: "#fff",
      icon: "info",
      order: 3,
      className: "chip-findings",
    },
  },
  [DOMAIN.AVAILABILITY]: {
    [CODES.AVAILABILITY.AVAILABLE]: {
      label: "Available",
      color: "rgba(76,175,80,.18)",
      text: "#2e7d32",
      order: 0,
    },
    [CODES.AVAILABILITY.UNAVAILABLE]: {
      label: "Unavailable",
      color: "rgba(244,67,54,.18)",
      text: "#b71c1c",
      order: 1,
    },
    [CODES.AVAILABILITY.MIXED]: {
      label: "Mixed",
      color: "rgba(255,193,7,.18)",
      text: "#e65100",
      order: 2,
    },
  },
  [DOMAIN.FLIGHT_TYPE]: {
    [CODES.FLIGHT_TYPE.TRAINING]: {
      label: "Training",
      color: "#90caf9",
      text: "#0d47a1",
      order: 0,
    },
    [CODES.FLIGHT_TYPE.SOLO]: {
      label: "Solo",
      color: "#a5d6a7",
      text: "#1b5e20",
      order: 1,
    },
    [CODES.FLIGHT_TYPE.XC]: {
      label: "Cross-country",
      color: "#ffe082",
      text: "#e65100",
      order: 2,
    },
  },
  [DOMAIN.FLIGHT_PRICE_CATEGORY]: {
    [CODES.FLIGHT_PRICE_CATEGORY.NONE]: {
      label: "Pas d'instructeur",
      color: "rgba(76,175,80,.18)",
      text: "#2e7d32",
      order: 0,
    },
    [CODES.FLIGHT_PRICE_CATEGORY.BASIC]: {
      label: "Instruction de base",
      color: "rgba(76,175,80,.18)",
      text: "#2e7d32",
      order: 0,
    },
    [CODES.FLIGHT_PRICE_CATEGORY.SOLO_SUP]: {
      label: "Supervision Solo",
      color: "rgba(76,175,80,.18)",
      text: "#2e7d32",
      order: 0,
    },
    [CODES.FLIGHT_PRICE_CATEGORY.NIGHT]: {
      label: "Instruction de nuit",
      color: "rgba(76,175,80,.18)",
      text: "#2e7d32",
      order: 0,
    },
    [CODES.FLIGHT_PRICE_CATEGORY.FI_I]: {
      label: "Instruction FI-I",
      color: "rgba(76,175,80,.18)",
      text: "#2e7d32",
      order: 0,
    },
    [CODES.FLIGHT_PRICE_CATEGORY.IR]: {
      label: "Instruction IR",
      color: "rgba(76,175,80,.18)",
      text: "#2e7d32",
      order: 0,
    },
  },
  [DOMAIN.LANDING_APPROACH_TYPE]: {
    [CODES.LANDING_APPROACH_TYPE.VISUAL]: {
      label: "Visual",
      color: "rgba(76,175,80,.18)",
      text: "#2e7d32",
      order: 0,
    },
    [CODES.LANDING_APPROACH_TYPE.PA_3D]: {
      label: "Procédure 3D",
      color: "rgba(76,175,80,.18)",
      text: "#2e7d32",
      order: 0,
    },
    [CODES.LANDING_APPROACH_TYPE.NPA_2D]: {
      label: "Procédure 2D",
      color: "rgba(76,175,80,.18)",
      text: "#2e7d32",
      order: 0,
    },
  },
};

/* 8) Provider en mémoire (remplaçable plus tard par une source API/tenant) */
export const InMemoryCatalogProvider: CatalogProvider = {
  get(locale: string) {
    const base = (locale || "").toLowerCase().split("-")[0];
    return base === "fr" ? FR : EN;
  },
};

/* 9) Helpers TS (si tu veux les appeler côté TS) */
function pickLocale(wanted: string) {
  const base = (wanted || "").toLowerCase().split("-")[0];
  return base || "en";
}
export function getView(domain: Domain, code: string, locale: string) {
  const reg = InMemoryCatalogProvider.get(pickLocale(locale));
  return reg?.[domain]?.[code];
}
export function getOrder(domain: Domain, code: string, locale: string) {
  return getView(domain, code, locale)?.order ?? 999;
}

/* 10) Pipe générique multi-langues (pour les templates) */
@Pipe({ name: "catalog", standalone: true, pure: true })
export class CatalogPipe implements PipeTransform {
  private LOCALE = inject(LOCALE_ID);
  private provider = InMemoryCatalogProvider; // ou inject(CATALOG_PROVIDER) si tu fournis autre chose

  transform<TField extends keyof View>(
    code: string | null | undefined,
    domain: Domain,
    field: TField,
    localeOverride?: string,
    fallback?: any
  ): View[TField] | string {
    if (!code) return fallback ?? "";
    const base = pickLocale(localeOverride ?? this.LOCALE);
    const v = this.provider.get(base)?.[domain]?.[code];
    return (v && (v[field] as any)) ?? fallback ?? code;
  }
}
